import React, { useState } from "react";
import { Plus, Trash2, CalendarDays } from "lucide-react";
import TimePicker from "@/components/TimePicker";

/**
 * Dynamic list of key life events. Each entry has:
 *   - date (YYYY-MM-DD via calendar picker)
 *   - time (HH:MM 24-hour, entered via 12H or 24H picker)
 *   - text (description)
 *
 * Stored on the form as [{ date, time, text }, ...]
 * Serialised to a readable string before POSTing so the backend
 * (which takes `life_events: string`) stays compatible.
 */
export default function LifeEvents({
  value,
  onChange,
  labels,
  testId = "life-events",
}) {
  const list = Array.isArray(value) ? value : [];
  const [fmt, setFmt] = useState("12"); // shared 12/24 toggle for all rows

  const add = () =>
    onChange([...list, { date: "", time: "", text: "" }]);
  const update = (i, patch) => {
    const next = list.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, k) => k !== i));

  return (
    <div className="space-y-3" data-testid={testId}>
      {list.length === 0 && (
        <p className="text-xs text-[color:var(--gold-soft)] opacity-80 font-sans-ui">
          {labels.empty}
        </p>
      )}

      {/* Shared time-format toggle — only shown when at least one row exists */}
      {list.length > 0 && (
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest text-[color:var(--gold-soft)] font-sans-ui">
            Time format
          </span>
          <div
            className="inline-flex rounded-full border overflow-hidden text-xs font-sans-ui"
            style={{ borderColor: "rgba(212,166,74,0.45)" }}
            data-testid={`${testId}-fmt`}
          >
            {["12", "24"].map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setFmt(mode)}
                className={`px-3 py-1 transition ${
                  fmt === mode
                    ? "bg-[color:var(--gold)] text-[color:var(--maroon-darker)] font-semibold"
                    : "text-[color:var(--gold-soft)]"
                }`}
                data-testid={`${testId}-fmt-${mode}`}
                aria-pressed={fmt === mode}
              >
                {mode === "12" ? "12H · AM/PM" : "24H"}
              </button>
            ))}
          </div>
        </div>
      )}

      {list.map((ev, i) => (
        <div
          key={i}
          className="p-4 rounded-xl border border-[rgba(212,166,74,0.25)] bg-[rgba(20,4,8,0.35)] space-y-3"
          data-testid={`${testId}-row-${i}`}
        >
          <div className="grid md:grid-cols-[180px_1fr_44px] gap-2 items-start">
            <div className="relative">
              <input
                type="date"
                className="input pr-9"
                value={ev.date || ""}
                onChange={(e) => update(i, { date: e.target.value })}
                data-testid={`${testId}-date-${i}`}
              />
              <CalendarDays
                size={14}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--gold-soft)] pointer-events-none"
              />
            </div>
            <input
              className="input"
              placeholder={labels.placeholder}
              value={ev.text || ""}
              onChange={(e) => update(i, { text: e.target.value })}
              data-testid={`${testId}-text-${i}`}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="btn-ghost !py-2 !px-3 flex items-center justify-center"
              aria-label="remove"
              data-testid={`${testId}-remove-${i}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
          <div className="grid md:grid-cols-[180px_1fr] gap-2 items-center">
            <span className="text-[10px] uppercase tracking-widest text-[color:var(--gold-soft)] font-sans-ui">
              {labels.timeLabel || "Approx. time"}
            </span>
            <TimePicker
              value={ev.time || ""}
              onChange={(v) => update(i, { time: v })}
              format={fmt}
              showToggle={false}
              testId={`${testId}-time-${i}`}
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="btn-ghost text-sm py-2 px-4"
        data-testid={`${testId}-add`}
      >
        <Plus size={14} /> {labels.add}
      </button>
    </div>
  );
}

// Helper: serialize the list into a readable multi-line string for backend.
export function serializeLifeEvents(list) {
  if (!Array.isArray(list) || list.length === 0) return null;
  const lines = list
    .filter(
      (e) =>
        (e.date || "").trim() ||
        (e.text || "").trim() ||
        (e.time || "").trim()
    )
    .map((e) => {
      const d = (e.date || "").trim();
      const tm = (e.time || "").trim();
      const t = (e.text || "").trim();
      const stamp = [d, tm].filter(Boolean).join(" ");
      if (stamp && t) return `${stamp} — ${t}`;
      return stamp || t;
    });
  return lines.length ? lines.join("\n") : null;
}
